/**
 * File landing.
 *
 * If the browser supports the File System Access API (Chrome/Edge on desktop),
 * the user picks a folder once and every arriving file is written straight into
 * it — no download prompt, no "(1)" suffixes, permission persists across
 * sessions. That is the single biggest reason this web app can feel like a
 * native one. Everywhere else we fall back to a normal download.
 */

const HANDLE_KEY = 'slipway.folder';

export function supportsDirectoryAccess(): boolean {
  return typeof (window as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';
}

/* The handle itself is stored in IndexedDB — it is a live object, not JSON, so
   localStorage cannot hold it. */
function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('slipway', 1);
    req.onupgradeneeded = () => req.result.createObjectStore('handles');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | null> {
  const db = await idb();
  return new Promise((resolve) => {
    const req = db.transaction('handles', 'readonly').objectStore('handles').get(key);
    req.onsuccess = () => resolve((req.result as T) ?? null);
    req.onerror = () => resolve(null);
  });
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await idb();
  await new Promise<void>((resolve) => {
    const req = db.transaction('handles', 'readwrite').objectStore('handles').put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
}

export async function chooseFolder(): Promise<FileSystemDirectoryHandle | null> {
  if (!supportsDirectoryAccess()) return null;
  try {
    const handle = await (
      window as unknown as {
        showDirectoryPicker: (o: unknown) => Promise<FileSystemDirectoryHandle>;
      }
    ).showDirectoryPicker({ mode: 'readwrite', id: 'slipway', startIn: 'downloads' });
    await idbSet(HANDLE_KEY, handle);
    return handle;
  } catch {
    return null; // user cancelled
  }
}

/** Returns the saved folder if we still hold write permission for it. */
export async function savedFolder(): Promise<FileSystemDirectoryHandle | null> {
  const handle = await idbGet<FileSystemDirectoryHandle>(HANDLE_KEY);
  if (!handle) return null;
  try {
    const perm = await (
      handle as unknown as { queryPermission: (o: unknown) => Promise<PermissionState> }
    ).queryPermission({ mode: 'readwrite' });
    return perm === 'granted' ? handle : null;
  } catch {
    return null;
  }
}

export async function forgetFolder(): Promise<void> {
  await idbSet(HANDLE_KEY, null);
}

/**
 * Collision policy, matching the spec: identical content is a no-op (you
 * already have the file), different content gets a ` (2)` suffix. Never
 * silently overwrite — that would be data loss.
 */
export function suffixName(filename: string, n: number): string {
  if (n <= 1) return filename;
  const dot = filename.lastIndexOf('.');
  if (dot <= 0) return `${filename} (${n})`;
  return `${filename.slice(0, dot)} (${n})${filename.slice(dot)}`;
}

export async function saveToFolder(
  dir: FileSystemDirectoryHandle,
  filename: string,
  blob: Blob,
  sameContent: (existing: File) => Promise<boolean>,
): Promise<{ written: boolean; name: string }> {
  for (let n = 1; n < 100; n++) {
    const name = suffixName(filename, n);
    let existing: FileSystemFileHandle | null = null;
    try {
      existing = await dir.getFileHandle(name);
    } catch {
      existing = null;
    }

    if (existing) {
      const file = await existing.getFile();
      // Already have these exact bytes — nothing to do.
      if (await sameContent(file)) return { written: false, name };
      continue; // different content, try the next suffix
    }

    const handle = await dir.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return { written: true, name };
  }
  throw new Error('Too many name collisions.');
}

/** Fallback for browsers without directory access. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function isHeic(filename: string, mime: string): boolean {
  return /\.hei[cf]$/i.test(filename) || /image\/hei[cf]/i.test(mime);
}

export function humanSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

export function relativeTime(ts: number, now: number = Date.now()): string {
  const s = Math.max(0, Math.floor((now - ts) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
